<template>
  <div class="row order-inline-items">
    <div class="col-12">
      <div class="order-inline-items__heading">
        <div>
          <h4 class="mb-1">Artículos del pedido</h4>
          <small class="text-muted">Los valores actuales se conservan mientras editás el encabezado.</small>
        </div>
        <small class="text-muted">Para cambiar un artículo, eliminá el renglón y agregá el correcto.</small>
      </div>
    </div>

    <div class="col-12 d-none d-md-block">
      <div class="order-inline-items__table">
        <div class="order-inline-items__row order-inline-items__head">
          <span>Artículo</span>
          <span>Cantidad</span>
          <span>Precio USD</span>
          <span>Costo dist.</span>
          <span>Importe USD</span>
          <span></span>
        </div>
        <div v-for="item in items" :key="item.id" class="order-inline-items__row">
          <div class="order-inline-items__article">
            <strong>{{ item.article.code }}</strong>
            <span>{{ item.article.description }}</span>
          </div>
          <el-input v-model="draftFor(item).quantity" type="number" min="1" :disabled="isSaving(item.id)" />
          <el-input v-model="draftFor(item).price" type="number" step="0.01" :disabled="isSaving(item.id)" />
          <el-input v-model="draftFor(item).distributorCost" type="number" step="0.01" :disabled="isSaving(item.id)" />
          <strong>{{ amountFor(item) }}</strong>
          <div class="order-inline-items__actions">
            <base-button
              native-type="button"
              size="sm"
              type="primary"
              :disabled="!isDirty(item) || isSaving(item.id)"
              @click="saveItem(item)"
            >
              <i v-if="isSaving(item.id)" class="fas fa-spinner fa-spin mr-1"></i>
              Guardar
            </base-button>
            <button
              v-if="isDirty(item)"
              type="button"
              class="btn btn-link btn-sm"
              :disabled="isSaving(item.id)"
              @click="resetItem(item)"
            >
              Cancelar
            </button>
          </div>
          <div v-if="rowErrors[item.id]" class="order-inline-items__error">{{ rowErrors[item.id] }}</div>
        </div>
      </div>
    </div>

    <div class="col-12 d-md-none">
      <div v-for="item in items" :key="`mobile-${item.id}`" class="order-inline-items__mobile">
        <strong>{{ item.article.code }} · {{ item.article.description }}</strong>
        <div class="row mt-2">
          <div class="col-4">
            <label>Cantidad</label>
            <el-input v-model="draftFor(item).quantity" type="number" min="1" :disabled="isSaving(item.id)" />
          </div>
          <div class="col-4">
            <label>Precio USD</label>
            <el-input v-model="draftFor(item).price" type="number" step="0.01" :disabled="isSaving(item.id)" />
          </div>
          <div class="col-4">
            <label>Costo dist.</label>
            <el-input
              v-model="draftFor(item).distributorCost"
              type="number"
              step="0.01"
              :disabled="isSaving(item.id)"
            />
          </div>
        </div>
        <div class="d-flex align-items-center justify-content-between mt-2">
          <strong>Importe: {{ amountFor(item) }} USD</strong>
          <div>
            <base-button
              native-type="button"
              size="sm"
              type="primary"
              :disabled="!isDirty(item) || isSaving(item.id)"
              @click="saveItem(item)"
            >
              Guardar
            </base-button>
            <button v-if="isDirty(item)" type="button" class="btn btn-link btn-sm" @click="resetItem(item)">
              Cancelar
            </button>
          </div>
        </div>
        <small v-if="rowErrors[item.id]" class="text-danger d-block mt-2">{{ rowErrors[item.id] }}</small>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    orderId: {
      type: Number,
      required: true,
    },

    items: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      drafts: {},
      rowErrors: {},
      savingItemIds: [],
    };
  },

  watch: {
    items: {
      immediate: true,
      handler(items) {
        items.forEach((item) => {
          if (!this.drafts[item.id]) {
            this.$set(this.drafts, item.id, this.draftFromItem(item));
          }
        });
      },
    },
  },

  methods: {
    draftFromItem(item) {
      return {
        quantity: item.quantity,
        price: item.price,
        distributorCost: item.distributorCost,
      };
    },

    draftFor(item) {
      if (!this.drafts[item.id]) {
        this.$set(this.drafts, item.id, this.draftFromItem(item));
      }

      return this.drafts[item.id];
    },

    isSaving(itemId) {
      return this.savingItemIds.includes(itemId);
    },

    isDirty(item) {
      const draft = this.draftFor(item);

      return (
        Number(draft.quantity) !== Number(item.quantity) ||
        Number(draft.price) !== Number(item.price) ||
        Number(draft.distributorCost) !== Number(item.distributorCost)
      );
    },

    amountFor(item) {
      const draft = this.draftFor(item);
      const amount = Number(draft.quantity || 0) * Number(draft.price || 0);

      return amount.toFixed(2);
    },

    resetItem(item) {
      this.$set(this.drafts, item.id, this.draftFromItem(item));
      this.$delete(this.rowErrors, item.id);
    },

    saveItem(item) {
      const draft = this.draftFor(item);
      const payload = {
        article: item.article,
        quantity: Number(draft.quantity),
        price: Number(draft.price),
        distributorCost: Number(draft.distributorCost),
      };

      if (!Number.isInteger(payload.quantity) || payload.quantity < 1) {
        this.$set(this.rowErrors, item.id, 'La cantidad debe ser un número entero mayor a cero.');
        return;
      }

      this.$delete(this.rowErrors, item.id);
      this.savingItemIds.push(item.id);

      this.$services
        .resource('order_items')
        .setOrder(this.orderId)
        .update(item.id, payload)
        .then((res) => {
          const updatedItem = res.data.data;
          this.$set(this.drafts, item.id, this.draftFromItem(updatedItem));
          this.$emit('updated', updatedItem);
          this.$success('Renglón actualizado', 'Los cambios se guardaron correctamente.');
        })
        .catch((errors) => {
          const message = Array.isArray(errors)
            ? errors[0]
            : 'No se pudo guardar el renglón. Revisá los valores e intentá nuevamente.';
          this.$set(this.rowErrors, item.id, message);
        })
        .finally(() => {
          this.savingItemIds = this.savingItemIds.filter((id) => id !== item.id);
        });
    },
  },
};
</script>

<style scoped>
.order-inline-items {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.order-inline-items__heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.order-inline-items__table {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  overflow: hidden;
}

.order-inline-items__row {
  display: grid;
  grid-template-columns: minmax(15rem, 2fr) minmax(6rem, 0.65fr) minmax(7rem, 0.8fr) minmax(7rem, 0.8fr) minmax(6rem, 0.7fr) minmax(9rem, 1fr);
  gap: 0.65rem;
  align-items: center;
  padding: 0.75rem;
  border-top: 1px solid #edf0f2;
}

.order-inline-items__head {
  border-top: 0;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: #172b4d;
}

.order-inline-items__article strong,
.order-inline-items__article span {
  display: block;
}

.order-inline-items__article span {
  color: #6c757d;
  font-size: 0.78rem;
}

.order-inline-items__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  white-space: nowrap;
}

.order-inline-items__error {
  grid-column: 2 / -1;
  margin-top: -0.2rem;
  color: #dc3545;
  font-size: 0.78rem;
}

.order-inline-items__mobile {
  margin-bottom: 0.75rem;
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}

.order-inline-items__mobile label {
  margin-bottom: 0.25rem;
  color: #6c757d;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
}

@media (max-width: 767px) {
  .order-inline-items__heading {
    display: block;
  }

  .order-inline-items__heading > small {
    display: block;
    margin-top: 0.5rem;
  }
}
</style>
